import type React from 'react';
import {useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField} from '@mui/material';
import type {Approver} from '../../../types/strategyTypes';

type Props = {
    /** Approver roles still waiting for a decision. */
    pending: Approver[];
    decision: 'approved' | 'returned';
    onDecide: (slot: string, comment: string) => Promise<unknown>;
    onClose: () => void;
};

/** Sign off, or return with comments, for one approver role. Mount it to open it. */
export const DecideDialog: React.FC<Props> = ({pending, decision, onDecide, onClose}) => {
    const [slot, setSlot] = useState<string>(pending[0]?.slot ?? '');
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);
    const returning = decision === 'returned';

    const submit = () => {
        setBusy(true);
        onDecide(slot, comment.trim()).then(onClose).catch(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="cp-decide-title">
            <DialogTitle id="cp-decide-title">{returning ? 'Return with comments' : 'Sign off the plan'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    <TextField select size="small" label="Signing as" value={slot} onChange={e => setSlot(e.target.value)}>
                        {pending.map(a => <MenuItem key={a.slot} value={a.slot}>{a.label}</MenuItem>)}
                    </TextField>
                    <TextField label={returning ? 'What needs to change' : 'Comment (optional)'} multiline minRows={3}
                               required={returning} value={comment} onChange={e => setComment(e.target.value)}
                               slotProps={{htmlInput: {maxLength: 2000}}}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" color={returning ? 'warning' : 'primary'} onClick={submit}
                        disabled={busy || !slot || (returning && !comment.trim())}>
                    {returning ? 'Return' : 'Sign off'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
